import { Injectable } from "@angular/core";
import { HttpClient, HttpParams } from "@angular/common/http";

import { loadModules } from "esri-loader";

import { GISConfig } from "../models/gis-config.model";


// Models
import { LunaDomain } from "../../builder/models/domain.model";

// Services
import { GisTokenService } from "../../gis/services/gis-token.service";
import { Point } from "go";

/*
 *  THIS SERVICE MEANT TO QUERY GIS AND WITHOUT GENERATING ALERTS
 *  TO QUERY GIS AND GENERATE ALERTS USE GisAlertService
 */
@Injectable()
export class GisQueryService {
    config: GISConfig;
    domains = new Array<LunaDomain>();
    domainStrings: any[];

    mapServerUrls = [
    ];

    constructor(
        public readonly tokenService: GisTokenService,
        public readonly httpClient: HttpClient
    ) {
        this.config = window["GIS.Conf"] as GISConfig;
        if (this.config) {
            this.mapServerUrls = [
                this.config.yearlyGISDataUrl,
                this.config.biWeeklyGISDataUrl,
                this.config.dailyGISDataUrl
            ];
        }
    }

    async getGISService(token: string, gisServiceUrl: string): Promise<any> {
        try {
            const [esriRequest] = await loadModules(["esri/request"]);
            const url = `${gisServiceUrl}?f=json&token=${token}`;
            const data = await esriRequest(url);
            return data;

        } catch (error) {
            console.log(`Error in ${gisServiceUrl}: ${error}`);
            throw error;
        }
    }

    async getGISServiceID(token: string, gisServiceUrl: string, layerName: string, exactLayerNameMatch: boolean = false): Promise<number> {
        const services = await this.getGISService(token, gisServiceUrl);
        let layerId = -1;
        if (services["data"].layers) {

            const applicableLayer = exactLayerNameMatch
                ? services["data"].layers.find(x => x.name.toString().toLowerCase() === layerName.toLowerCase())
                : services["data"].layers.find(x => x.name.toString().toLowerCase().includes(layerName.toLowerCase()));
            layerId = applicableLayer.id;
        }
        return layerId;
    }

    async identifyAtPoint(gisServiceUrl: string, point: number[]): Promise<any> {
		try {

			return this.tokenService.getToken().then(async response => {
				const [imageService, ImageIdentifyParameters, Point] = await loadModules([
					"esri/rest/imageService",
					"esri/rest/support/ImageIdentifyParameters",
					"esri/geometry/Point"
				]);

   
				const testPoint = new Point({
					latitude: point[0],
					longitude: point[1]
				});

				const params = new ImageIdentifyParameters();
				params.geometry = testPoint;
				params.returnGeometry = false;
				let queryUrl = `${gisServiceUrl}/?f=json&token=${response}`;

				return imageService.identify(queryUrl, params).then(result => {
					if (result.value) {
						return result;
					} else {
						return null;
					}
				}, (reject) => {
					console.error("GIS query rejected");
					reject(reject);
				});
			});
        } catch (error) {
            console.log(`error in ${gisServiceUrl}: ${error}`);
            throw error;
        }
    }

    async resolveQueryAtPoint(gisServiceUrl: string, layerName: string, point: number[], exactLayerNameMatch: boolean = false): Promise<any> {
        return this.resolvePointQueryRelationship(gisServiceUrl, layerName, point, "intersects", exactLayerNameMatch);
    };

    async getPopulationByCoordinate(x: number, y: number): Promise<string> {
        var error;
        try {
            return this.tokenService.getToken().then(async response => {
                const [Query, QueryTask, Point] = await loadModules([
                    "esri/tasks/support/Query",
                    "esri/tasks/QueryTask",
                    "esri/geometry/Point"
                ]);

                let featureLayerUrl = this.config.populationGISLayer + "/" + "?f=json&token=" + response;
                let testQueryTask = new QueryTask({
                    url: featureLayerUrl
                });
                const testPoint = new Point({
                    latitude: y,
                    longitude: x
                });
                const query = new Query();
                query.geometry = testPoint;
                query.spatialRelationship = "intersects";
                query.returnGeometry = false;
                query.outFields = ["*"];
                return testQueryTask.execute(query).then(result => {
                    if (result.features.length) {
						let population = result.features[0].attributes["POPULATION"] != null ? result.features[0].attributes["POPULATION"] : result.features[0].attributes["POP_2020"];
						return {
							Population: population,
                            ZoneType: result.features[0].attributes["TYPE"]
                        };
                    } else {
                        return {
                            Population: "NONURB",
                            ZoneType: ""
                        };
                    }
                }, reject => { return { Population: "ERROR", ZoneType: "ERROR" }; });
            });
        } catch (error) {
            console.log(`We have an error: ${error}`);
        }
    }

    async resolveBufferQueryAtPoint(gisServiceUrl: string, layerName: string, point: number[], buffer: number, whereClause: string = ""): Promise<any> {
        return this.tokenService.getToken().then(async token => {
            return new Promise((resolve) => {
                this.getGISService(token, gisServiceUrl).then(async gisService => {
                    if (gisService["data"].layers) {
                        // Get layer from feature server
                        const applicableLayer =
                            gisService["data"].layers.find(x => x.name.toString().toLowerCase().includes(layerName.toLowerCase()));
                        if (applicableLayer == null) {
                            console.error(`unable to find applicable layer ${layerName}`);
                            resolve(null);
                        }
                        var error;
                        try {
                            const [Query, QueryTask, Point] = await loadModules([
                                "esri/tasks/support/Query",
                                "esri/tasks/QueryTask",
                                "esri/geometry/Point"
                            ]);

                            gisServiceUrl =
                                gisServiceUrl + "/" + applicableLayer.id + "?f=json&token=" + token;


                            const testQueryTask = new QueryTask({
                                url: gisServiceUrl
                            });
                            const testPoint = new Point({
                                latitude: point[0],
                                longitude: point[1]
                            });


                            const query = new Query();

                            query.spatialRelationship = "intersects";
                            query.distance = buffer;
                            query.units = "feet";
                            query.geometry = testPoint;

                            query.returnGeometry = false;

                            if (whereClause && whereClause.length > 0) {
                                query.where = whereClause;
                            }

                            query.outFields = ["*"];

                            testQueryTask.execute(query).then(result => {
                                if (result.features && result.features.length > 0) {
                                    resolve(result);
                                } else {
                                    resolve("");
                                }
                            }, (reject) => {
                                console.error(`GIS query rejected: ${gisServiceUrl}`);
                                reject(reject);
                            });

                        } catch (error) {
                            console.log(`Error in ${gisServiceUrl}: ${error}`);
                            throw error;
                        }

                    }
                });
            });
        });
    }
    async resolvePointQueryRelationship(gisServiceUrl: string, layerName: string, point: number[], relationship: string, exactLayerNameMatch: boolean = false): Promise<any> {
        const token = await this.tokenService.getToken();
        const layerId = await this.getGISServiceID(token, gisServiceUrl, layerName, exactLayerNameMatch);
        try {
            const [Query, QueryTask, Point] = await loadModules([
                "esri/tasks/support/Query",
                "esri/tasks/QueryTask",
                "esri/geometry/Point"
            ]);

            gisServiceUrl = `${gisServiceUrl}/${layerId}?f=json&token=${token}`;

            const queryTask = new QueryTask({ url: gisServiceUrl, requestOptions: { authMode: 'no-prompt' } });
            const queryPoint = new Point({ latitude: point[0], longitude: point[1] });
            const query = new Query();
            query.spatialRelationship = relationship;
            query.geometry = queryPoint;
            query.returnGeometry = false;
            query.outFields = ["*"];
            const result = await queryTask.execute(query);
            return result;
        } catch (error) {
            console.error(`Error in ${gisServiceUrl}: ${error}`);
            throw error;
        }

    }

    async queryImageServerAtPoint(gisServiceUrl: string, point: number[]): Promise<any> {
        try {
			
			return this.tokenService.getToken().then(async response => {
				const [imageService, ImageIdentifyParameters, Point] = await loadModules([
					"esri/rest/imageService",
					"esri/rest/support/ImageIdentifyParameters",
					"esri/geometry/Point"
				]);
				const testPoint = new Point({
					latitude: point[0],
					longitude: point[1]
				});
				let params = new ImageIdentifyParameters();
				params.geometry = testPoint;
				params.returnGeometry = false;
				let queryUrl = `${gisServiceUrl}/?f=json&token=${response}`;

				return imageService.identify(queryUrl, params).then(result => {
					JSON.stringify(result);
					return result;
				}, reject => {
					console.error(`GIS query rejected: ${gisServiceUrl}`);
					reject(reject);
				});
			});           
        } catch (error) {
            console.error(`Error in ${gisServiceUrl}: ${error}`);
            throw error;
        }
    }

    async queryLayerAtPoint_Unauthenticated(gisServiceUrl: string, point: number[]): Promise<any> {
        return new Promise((resolve) => {
            var error;
            try {

                loadModules(["esri/tasks/support/Query", "esri/tasks/QueryTask", "esri/geometry/Point"]).then(([
                    Query, QueryTask, Point
                ]) => {
                    const testQueryTask = new QueryTask({
                        url: gisServiceUrl
                    });
                    const testPoint = new Point({
                        latitude: point[0],
                        longitude: point[1]
                    });


                    const query = new Query();

                    query.spatialRelationship = "intersects";
                    query.geometry = testPoint;

                    query.returnGeometry = false;
                    query.outFields = ["*"];

                    testQueryTask.execute(query).then(result => {
                        if (result.features && result.features.length > 0) {
                            resolve(result);
                        }
                        else
                            resolve("");
                    },
                        reject => {
                            console.error(`GIS query rejected: ${gisServiceUrl}`);
                        reject(reject);
                    });
                });

            } catch (error) {
                console.error(`Error in ${gisServiceUrl}: ${error}`);
                throw error;
            }
        });
    }

    async queryBufferPolyline(gisServiceUrl: string, layerName: string, points: number[][], buffer: number): Promise<any> {
        return this.tokenService.getToken().then(async token => {
            return new Promise((resolve) => {
                this.getGISService(token, gisServiceUrl).then(async gisService => {
                    if (gisService["data"].layers) {

                        const applicableLayer =
                            gisService["data"].layers.find(x => x.name.toString().toLowerCase().includes(layerName.toLowerCase()));

                        const [Query, QueryTask, Polyline] = await loadModules([
                            "esri/tasks/support/Query",
                            "esri/tasks/QueryTask",
                            "esri/geometry/Polyline"
                        ]);

                        gisServiceUrl =
                            gisServiceUrl + "/" + applicableLayer.id + "?f=json&token=" + token;


                        const testQueryTask = new QueryTask({
                            url: gisServiceUrl
                        });
                        const testPoint = new Polyline({
                            hasZ: points[0].length === 3 ? true : false,
                            hasM: points[0].length === 4 ? true : false,
                            paths: points
                        });

                        const query = new Query();
                        query.spatialRelationship = "intersects";
                        query.distance = buffer;
                        query.units = "feet";
                        query.geometry = testPoint;
                        query.returnGeometry = false;
                        query.outFields = ["*"];

                        testQueryTask.execute(query).then(result => {
                            resolve(result);
                        }, (reject) => {
                            console.error(`GIS query rejected: ${gisServiceUrl}`);
                            reject(reject);
                        });
                    }
                });
            });
        });
    }

    async queryBufferAtPoint(gisServiceUrl: string, layerName: string, point: number[], buffer: number): Promise<any> {
        return this.tokenService.getToken().then(async token => {
            return new Promise((resolve) => {
                this.getGISService(token, gisServiceUrl).then(async gisService => {
                    if (gisService["data"].layers) {
                        // Get layer from feature server
                        const applicableLayer =
                            gisService["data"].layers.find(x => x.name.toString().toLowerCase().includes(layerName.toLowerCase()));

                        var error;
                        try {
                            const [Query, QueryTask, Point] = await loadModules([
                                "esri/tasks/support/Query",
                                "esri/tasks/QueryTask",
                                "esri/geometry/Point"
                            ]);

                            gisServiceUrl =
                                gisServiceUrl + "/" + applicableLayer.id + "?f=json&token=" + token;


                            const testQueryTask = new QueryTask({
                                url: gisServiceUrl
                            });
                            const testPoint = new Point({
                                latitude: point[0],
                                longitude: point[1]
                            });


                            const query = new Query();

                            query.spatialRelationship = "intersects";
                            query.distance = buffer;
                            query.units = "feet";
                            query.geometry = testPoint;

                            query.returnGeometry = false;
                            query.outFields = ["*"];

                            testQueryTask.execute(query).then(result => {
                                if (result.features && result.features.length > 0) {
                                    resolve(result.features[0].attributes);
                                } else {
                                    resolve("");
                                }
                            }, (reject) => {
                                console.error(`GIS query rejected: ${gisServiceUrl}`);
                                reject(reject);
                            });

                        } catch (error) {
                            console.error(`Error in ${gisServiceUrl}: ${error}`);
                            throw error;
                        }

                    }
                });
            });
        });
    }
}